import { z } from 'zod';

/**
 * Персона (§5-7, §11).
 *
 * One Capture First (§2.3): одна запись закрывает аватар, голос, референсы
 * мимики и кадры внешности. Отдельной записи голоса в онбординге нет (§80).
 */

export const PersonaStatus = {
  DRAFT: 'draft',
  CHECKING: 'checking',
  REJECTED: 'rejected',
  PROCESSING: 'processing',
  READY: 'ready',
  BLOCKED: 'blocked',
} as const;
export type PersonaStatus = (typeof PersonaStatus)[keyof typeof PersonaStatus];

/** §5: три блока со статусами. */
export interface PersonaPartView {
  key: 'avatar' | 'voice' | 'appearance';
  label: string;
  ready: boolean;
  status: string;
}

export interface PersonaView {
  id: string;
  status: PersonaStatus;
  hasCapture: boolean;
  captureAssetId: string | null;
  captureUrl: string | null;
  parts: PersonaPartView[];
  appearanceCount: number;
  lastCheck: CaptureCheckView | null;
}

export type CheckVerdict = 'pass' | 'warn' | 'fail';

export interface CaptureIssue {
  code: string;
  /** Готовый текст §52: что не так и что с этим делать. */
  message: string;
  severity: 'fail' | 'warn';
}

export interface CaptureCheckView {
  id: string;
  verdict: CheckVerdict;
  issues: CaptureIssue[];
  /** Пригодность аудио для клонирования голоса — отдельный вердикт (§7). */
  voiceCloneVerdict: CheckVerdict;
  createdAt: string;
}

/** §6.3: ориентир 1-3 минуты, требование конфигурируемое. */
/**
 * Сколько нужно записывать (§6.3).
 *
 * Длина нужна голосу, и только ему. Аватар делается по одному кадру — самому
 * удачному из записи. Кадры внешности берутся из разных мест, но их пять.
 * Провайдеру голоса хватает тридцати секунд, лучше — от минуты.
 *
 * Отсюда цифры: минимум с запасом над провайдерским порогом, ориентир —
 * в рекомендованной полосе, потолок — вдвое меньше прежнего. Раньше стояло
 * 60/120/180, и две минуты чтения вслух ради одного кадра и минуты звука
 * человек тратил зря.
 *
 * §10.1 остаётся: голос можно улучшить отдельной записью, не переснимая всё.
 */
export const captureDuration = {
  minSec: 45,
  targetSec: 90,
  maxSec: 150,
} as const;

export const submitCaptureSchema = z
  .object({
    assetId: z.string().min(1),
    scriptVersion: z.string().min(1).optional(),
  })
  .strict();

export const uploadAppearanceSchema = z
  .object({
    assetId: z.string().min(1),
    kind: z.enum([
      'face_frontal',
      'face_turn',
      'expression',
      'half_body',
      'full_body',
      'uploaded_photo',
    ]),
  })
  .strict();

export interface AppearanceAssetView {
  id: string;
  assetId: string;
  kind: string;
  source: string;
  qualityScore: number;
  url: string | null;
  createdAt: string;
}

/** Чего не хватает конкретной задаче (§11.2, §22). */
export interface AppearanceRequirement {
  kind: string;
  label: string;
  reason: string;
}

/**
 * Подготовка к динамичным сценам (§22).
 *
 * Два коротких видео вместо двадцати фотографий: повороты головы и полный
 * рост. Кадры извлекает система — просить снимки руками значит не получить
 * ни одного.
 */
export const dynamicPrepSchema = z
  .object({
    faceAssetId: z.string().min(1),
    bodyAssetId: z.string().min(1),
  })
  .strict();

export type DynamicPrepInput = z.infer<typeof dynamicPrepSchema>;

export interface DynamicPrepView {
  /** Материала хватает для динамичных сцен. */
  ready: boolean;
  /** Сколько кадров внешности собрано. */
  frames: number;
  hasFullBody: boolean;
  /** Какие стороны поворота уже покрыты. */
  bands: string[];
  /** Идёт ли разбор записей прямо сейчас. */
  processing: boolean;
}

/**
 * Съёмка подготовки к динамичным сценам (§22).
 *
 * Две короткие записи вместо двадцати снимков: в первой человек поворачивает
 * голову, во второй поворачивается сам.
 *
 * Команды идут по времени, потому что читать инструкцию и одновременно
 * поворачиваться невозможно. На первой же живой съёмке выяснилось, что
 * и этого мало: текст всё равно приходится читать, а на записи в полный
 * рост человек стоит в двух метрах от экрана и не видит вообще ничего.
 * Поэтому команда короткая (одно-два слова, чтобы читалась издалека)
 * и произносится вслух — слушать можно, не глядя на экран.
 *
 * Разбег в начале — оттуда же: запись уже идёт, пока человек отходит
 * от камеры и устраивается. Без него первая команда звучала в пустой кадр.
 */
export interface PrepCue {
  /** С какой секунды показывать. */
  atSec: number;
  key: string;
}

export interface PrepTake {
  id: 'face' | 'body';
  durationSec: number;
  minSec: number;
  /** Сколько секунд в начале человек устраивается: команды ещё не идут. */
  leadInSec: number;
  cues: PrepCue[];
}

/** Собрать дубль: команды через равные промежутки после разбега. */
const take = (id: PrepTake['id'], leadInSec: number, stepSec: number, keys: string[]): PrepTake => {
  const cues = [
    { atSec: 0, key: 'ready' },
    ...keys.map((key, index) => ({ atSec: leadInSec + index * stepSec, key })),
  ];
  const last = cues[cues.length - 1] as PrepCue;

  return {
    id,
    leadInSec,
    // Последней команде тоже нужно время: обрывать запись на ней бессмысленно.
    durationSec: last.atSec + stepSec,
    minSec: leadInSec + stepSec * 2,
    cues,
  };
};

export const dynamicPrepTakes: PrepTake[] = [
  // Голову человек поворачивает быстро — четырёх секунд на команду хватает.
  take('face', 4, 4, [
    'straight',
    'turnLeft',
    'turnRight',
    'profileLeft',
    'profileRight',
    'upDown',
    'expressions',
  ]),
  /**
   * Разбег вдвое длиннее: чтобы попасть в кадр целиком, надо отойти на пару
   * метров, а это не мгновение. И шаг больше — поворот всем телом медленнее
   * поворота головы, а быстрый даёт смазанные кадры.
   */
  take('body', 8, 5, ['standFull', 'sideOne', 'back', 'sideTwo', 'faceAgain']),
];

/** Какая подсказка идёт сейчас: последняя, чьё время уже наступило. */
export function prepCueAt(take: PrepTake, elapsedSec: number): PrepCue {
  let current = take.cues[0] as PrepCue;
  for (const cue of take.cues) {
    if (elapsedSec >= cue.atSec) current = cue;
  }
  return current;
}

/** Сколько секунд до следующей команды. Ноль — если она последняя. */
export function prepSecondsToNext(take: PrepTake, elapsedSec: number): number {
  const next = take.cues.find((cue) => cue.atSec > elapsedSec);
  return next ? Math.max(0, Math.ceil(next.atSec - elapsedSec)) : 0;
}
